import { AlertTriangle, Check, ChevronDown, ChevronRight, CircleHelp, X } from "lucide-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { mentionKeyOf, titleOf, verdictTextOf } from "./indicationHighlights";
import type { IndicationVerdictView } from "./useIndicationVerdicts";

interface IndicationsPanelProps {
  verdicts: IndicationVerdictView[];
  /** Keys (`position:mentionIndex`) of the mentions whose text the document viewer could not find. */
  unfoundMentionKeys: ReadonlySet<string>;
  onOpenMention: (position: number, mentionIndex: number) => void;
}

const iconOf = (verdict: IndicationVerdictView) => {
  if (verdict.isNotClassified) return AlertTriangle;

  return verdict.verdict === "coded" ? Check : X;
};

const codedFirst = (verdicts: readonly IndicationVerdictView[]) =>
  [...verdicts].sort((a, b) => Number(b.verdict === "coded") - Number(a.verdict === "coded"));

const entriesLabelOf = (count: number) => `${count} ${count === 1 ? "entry" : "entries"}`;

type MentionSourceProps = {
  source: string;
  fileIdentifier: string | null;
  isUnfound: boolean;
  onOpen: () => void;
};

const MentionSource = ({ source, fileIdentifier, isUnfound, onOpen }: MentionSourceProps) => {
  const note = fileIdentifier === null ? "(not shown as a document)" : isUnfound ? "· source text not found" : null;

  if (note === null) {
    return (
      <button type="button" className="text-sky-700 underline" onClick={onOpen}>
        {source}
      </button>
    );
  }

  return (
    <span className="flex items-center gap-1 text-muted-foreground">
      <CircleHelp aria-hidden className="h-3 w-3" />
      {source} {note}
    </span>
  );
};

const IndicationCard = ({
  verdict,
  unfoundMentionKeys,
  onOpenMention,
}: { verdict: IndicationVerdictView } & Omit<IndicationsPanelProps, "verdicts">) => {
  const [openOverride, setOpenOverride] = useState<boolean>();
  const isCoded = verdict.verdict === "coded";
  const hasMentions = verdict.mentions.length > 0;
  const hasUnfoundMention = verdict.mentions.some((_, mentionIndex) =>
    unfoundMentionKeys.has(mentionKeyOf(verdict.position, mentionIndex)),
  );
  const isOpen = openOverride ?? hasUnfoundMention;
  const Chevron = isOpen ? ChevronDown : ChevronRight;
  const verdictText = verdictTextOf(verdict);
  const VerdictIcon = iconOf(verdict);

  return (
    <li className="rounded-md border p-3" data-testid="indication-row">
      <Collapsible open={isOpen} onOpenChange={setOpenOverride}>
        <div className="flex items-start gap-2">
          <Badge
            variant={isCoded ? "default" : "outline"}
            className="shrink-0 px-1.5"
            title={verdictText}
            aria-label={verdictText}
            role="img"
          >
            <VerdictIcon aria-hidden className="h-3.5 w-3.5" />
          </Badge>
          <p
            className={cn(
              "line-clamp-2 min-w-0 flex-1",
              isCoded ? "font-semibold" : "text-muted-foreground line-through",
            )}
            data-testid="indication-title"
          >
            {titleOf(verdict)}
          </p>
          {hasMentions && (
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="flex shrink-0 items-center gap-0.5 text-xs text-muted-foreground hover:text-foreground"
              >
                {hasUnfoundMention && <AlertTriangle aria-label="source text not found" className="h-3 w-3" />}
                <Chevron aria-hidden className="h-3 w-3" />
                {entriesLabelOf(verdict.mentions.length)}
              </button>
            </CollapsibleTrigger>
          )}
        </div>

        <div className="mt-1 flex flex-wrap items-center gap-2">
          <p
            className="font-mono text-xs text-muted-foreground"
            title={verdict.ruleDefinition ?? undefined}
            data-testid="indication-reason"
          >
            🤖 {verdict.reason}
          </p>
          {verdict.isNotClassified && <Badge variant="secondary">not classified</Badge>}
          {verdict.personalHistory && <Badge variant="secondary">personal history</Badge>}
          {verdict.absent && <Badge variant="secondary">absent</Badge>}
        </div>

        {!hasMentions && <p className="mt-2 text-xs text-muted-foreground">quote not stored</p>}

        <CollapsibleContent asChild>
          <ul className="mt-2 space-y-2">
            {verdict.mentions.map((mention, mentionIndex) => {
              const key = mentionKeyOf(verdict.position, mentionIndex);

              return (
                <li key={key} className="text-xs">
                  {mention.text && <q className="italic">{mention.text}</q>}
                  <div>
                    <MentionSource
                      source={mention.source}
                      fileIdentifier={mention.fileIdentifier}
                      isUnfound={unfoundMentionKeys.has(key)}
                      onOpen={() => onOpenMention(verdict.position, mentionIndex)}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        </CollapsibleContent>
      </Collapsible>
    </li>
  );
};

export const IndicationsPanel = ({ verdicts, unfoundMentionKeys, onOpenMention }: IndicationsPanelProps) => (
  <ul className="space-y-3 text-sm" data-testid="indications-panel">
    {codedFirst(verdicts).map((verdict) => (
      <IndicationCard
        key={verdict.position}
        verdict={verdict}
        unfoundMentionKeys={unfoundMentionKeys}
        onOpenMention={onOpenMention}
      />
    ))}
  </ul>
);
